import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { adminDomainAPI, branchAPI } from '../../services/api';
import { useAuthStore } from '../../store';

/**
 * Tenant Profile → Admin Address: the tenant's own admin-panel address — a
 * subdomain (merit.hotwifi.in) or its own domain (wifi.merit.com) — and one per
 * branch if wanted. There the sign-in page and the panel show this tenant's
 * name and logo (Branding tab), and only its own admins sign in. The superadmin
 * gives and removes them; the tenant's admins see theirs.
 */
const errorOf = (err, fallback) => err.response?.data?.message || fallback;

function StatusBadge({ ok, pending, children }) {
    const kind = ok ? 'success' : pending ? 'warning' : 'danger';
    const icon = ok ? 'fa-check-circle' : pending ? 'fa-hourglass-half' : 'fa-exclamation-circle';
    return (
        <span className={`badge badge-${kind}`}>
            <i className={`fas ${icon}`} aria-hidden="true"></i> {children}
        </span>
    );
}

const AdminAddressPanel = ({ tenantId }) => {
    const isSuper = useAuthStore((s) => s.admin?.role === 'superadmin');
    const [domains, setDomains] = useState([]);
    const [meta, setMeta] = useState({ zone: null, target: '', managed: false });
    const [statuses, setStatuses] = useState({});
    const [branches, setBranches] = useState([]);
    const [form, setForm] = useState({ address: '', branchId: '' });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState('');

    const checkOne = useCallback(async (id) => {
        setStatuses((s) => ({ ...s, [id]: { ...s[id], checking: true } }));
        try {
            const res = await adminDomainAPI.status(id);
            setStatuses((s) => ({ ...s, [id]: res.data.data }));
        } catch (err) {
            setStatuses((s) => ({ ...s, [id]: { error: errorOf(err, 'Could not check this address') } }));
        }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await adminDomainAPI.list(tenantId);
            const list = res.data.data || [];
            setDomains(list);
            setMeta({ zone: res.data.zone, target: res.data.target, managed: res.data.managed });
            list.forEach((d) => checkOne(d._id));
        } catch (err) {
            toast.error(errorOf(err, 'Failed to load the admin addresses'));
        } finally {
            setLoading(false);
        }
    }, [tenantId, checkOne]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!isSuper) return;
        branchAPI.getAll(tenantId, { limit: 500 })
            .then((res) => {
                const data = res.data?.data;
                setBranches(Array.isArray(data) ? data : data?.branches || []);
            })
            .catch(() => setBranches([]));
    }, [tenantId, isSuper]);

    const preview = (() => {
        const value = form.address.trim().toLowerCase();
        if (!value) return '';
        return value.includes('.') || !meta.zone ? value : `${value}.${meta.zone}`;
    })();

    const add = async (e) => {
        e.preventDefault();
        if (!form.address.trim()) return;
        setBusy('add');
        try {
            const res = await adminDomainAPI.create({ tenantId, branchId: form.branchId || undefined, address: form.address.trim() });
            const { provisioning } = res.data;
            if (provisioning?.applied || !provisioning?.managed) toast.success(`${res.data.data.hostname} added`);
            else toast.warning(provisioning.message);
            setForm({ address: '', branchId: '' });
            await load();
        } catch (err) {
            toast.error(errorOf(err, 'Failed to add the address'));
        } finally {
            setBusy('');
        }
    };

    const setUpAgain = async (domain) => {
        setBusy(domain._id);
        try {
            const res = await adminDomainAPI.provision(domain._id);
            const { provisioning } = res.data;
            if (provisioning.applied) toast.success(provisioning.message);
            else toast.warning(provisioning.message);
            await checkOne(domain._id);
            setDomains((list) => list.map((d) => (d._id === domain._id ? { ...d, ...res.data.data } : d)));
        } catch (err) {
            toast.error(errorOf(err, 'Failed to set up the address'));
        } finally {
            setBusy('');
        }
    };

    const remove = async (domain) => {
        if (!window.confirm(`Remove ${domain.hostname}? Its admins sign in at ${meta.target} afterwards.`)) return;
        setBusy(domain._id);
        try {
            await adminDomainAPI.remove(domain._id);
            toast.success(`${domain.hostname} removed`);
            await load();
        } catch (err) {
            toast.error(errorOf(err, 'Failed to remove the address'));
        } finally {
            setBusy('');
        }
    };

    const takenBranches = new Set(domains.map((d) => String(d.branchId?._id || d.branchId || '')));

    return (
        <div className="admin-address">
            <div className="resource-card-modern">
                <div className="card-header">
                    <h3>Admin Address</h3>
                    <p className="text-sm text-muted">
                        This company's own address for the admin panel. There the sign-in page and the panel show
                        its name and logo (Branding tab) instead of HotWiFi's, and only its own admins can sign in.
                    </p>
                </div>
                <div className="card-body">
                    {loading ? (
                        <p className="text-muted">Loading…</p>
                    ) : domains.length === 0 ? (
                        <p className="text-muted">
                            No address of its own yet — its admins sign in at {meta.target || 'the platform address'}.
                        </p>
                    ) : (
                        <ul className="admin-address-list">
                            {domains.map((d) => {
                                const st = statuses[d._id];
                                return (
                                    <li key={d._id} className="admin-address-item">
                                        <div className="admin-address-main">
                                            <a href={`https://${d.hostname}`} target="_blank" rel="noopener noreferrer" className="admin-address-host">
                                                {d.hostname} <i className="fas fa-external-link-alt" aria-hidden="true"></i>
                                            </a>
                                            <span className="text-muted text-sm">
                                                {d.branchId ? `Branch: ${d.branchId.name || ''}` : 'Whole company'}
                                                {' · '}
                                                {d.custom ? "the company's own domain" : 'subdomain'}
                                            </span>
                                        </div>
                                        <div className="admin-address-state">
                                            {!st || st.checking ? (
                                                <span className="text-muted text-sm">Checking…</span>
                                            ) : st.error ? (
                                                <StatusBadge ok={false}>{st.error}</StatusBadge>
                                            ) : (
                                                <>
                                                    <StatusBadge ok={st.dns.ok}>{st.dns.ok ? 'DNS points here' : 'DNS not pointed here'}</StatusBadge>
                                                    {st.managed ? (
                                                        <StatusBadge ok={st.certificate.ready} pending={st.dns.ok && !st.certificate.ready}>
                                                            {st.certificate.ready ? 'Certificate ready' : 'No certificate yet'}
                                                        </StatusBadge>
                                                    ) : (
                                                        <span className="badge badge-gray">Ingress set up by hand</span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        {st && !st.checking && !st.error && (st.dns.message || (st.managed && !st.certificate.ready)) && (
                                            <p className="admin-address-note text-sm">
                                                {st.dns.message || st.lastError || st.certificate.message}
                                            </p>
                                        )}
                                        <div className="admin-address-actions">
                                            <button type="button" className="btn-secondary-modern btn-sm" onClick={() => checkOne(d._id)} disabled={busy === d._id}>
                                                <i className="fas fa-sync-alt"></i> Check
                                            </button>
                                            {isSuper && meta.managed && (
                                                <button type="button" className="btn-secondary-modern btn-sm" onClick={() => setUpAgain(d)} disabled={busy === d._id}>
                                                    <i className="fas fa-redo"></i> Set up again
                                                </button>
                                            )}
                                            {isSuper && (
                                                <button type="button" className="btn-danger-modern btn-sm" onClick={() => remove(d)} disabled={busy === d._id}>
                                                    <i className="fas fa-trash"></i> Remove
                                                </button>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>

            {isSuper && (
                <div className="resource-card-modern">
                    <div className="card-header">
                        <h3>Add an address</h3>
                        <p className="text-sm text-muted">
                            {meta.zone ? <>A subdomain name (<code>merit</code> → <code>merit.{meta.zone}</code>) or </> : 'The '}
                            the company's own domain (<code>wifi.merit.com</code>).
                        </p>
                    </div>
                    <div className="card-body">
                        <form onSubmit={add} className="form-grid">
                            <div className="form-group">
                                <label htmlFor="admin-address-input">Address</label>
                                <input
                                    id="admin-address-input"
                                    type="text"
                                    value={form.address}
                                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                                    placeholder={meta.zone ? 'merit  or  wifi.merit.com' : 'wifi.merit.com'}
                                    autoComplete="off"
                                    spellCheck={false}
                                />
                                {preview && <small className="text-muted">Admins will open https://{preview}</small>}
                            </div>
                            <div className="form-group">
                                <label htmlFor="admin-address-scope">For</label>
                                <select
                                    id="admin-address-scope"
                                    value={form.branchId}
                                    onChange={(e) => setForm({ ...form, branchId: e.target.value })}
                                >
                                    <option value="" disabled={takenBranches.has('')}>The whole company</option>
                                    {branches.map((b) => (
                                        <option key={b._id} value={b._id} disabled={takenBranches.has(String(b._id))}>
                                            Branch: {b.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group span-2">
                                <button type="submit" className="btn-primary-modern" disabled={busy === 'add' || !form.address.trim()}>
                                    {busy === 'add' ? 'Adding…' : 'Add address'}
                                </button>
                            </div>
                        </form>

                        <div className="admin-address-help text-sm">
                            <strong>DNS</strong>
                            <ul>
                                {meta.zone && (
                                    <li>Subdomains: one <code>*.{meta.zone}</code> A record pointing where <code>{meta.target}</code> points covers them all.</li>
                                )}
                                <li>The company's own domain: they add a CNAME record, for example <code>wifi.merit.com → {meta.target}</code>.</li>
                                <li>
                                    {meta.managed
                                        ? 'Once the DNS points here, the address and its SSL certificate are set up by themselves (a minute or two) — press “Set up again” if the DNS was added later.'
                                        : 'This server is not in Kubernetes: add the address to the web server by hand.'}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminAddressPanel;
